import { useState, useEffect } from 'react';
import { Spin, message, Space, Modal, Typography, Tooltip, Tag, Button, Input, Badge, Card, Switch, Alert, Progress, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import './Entity.css';
import {
    CloseOutlined,
    UserAddOutlined, ClockCircleOutlined,
    ReadOutlined, MessageOutlined, MailOutlined,
    CopyOutlined, DeleteOutlined, LinkOutlined, SettingOutlined,
} from '@ant-design/icons';
import InlineEditField, { isBlank } from './InlineEditField';
import ConnectedEntities from './ConnectedEntities';
import LeaveEntityButton, { isSoleAdmin } from './LeaveEntityButton';
import InviteEmailModal from './InviteEmailModal';
import EntityComms from './EntityComms';
import EntityImageEditor from './EntityImageEditor';
import PinToSidebarButton from '../Sidebar/PinToSidebarButton';
import { useAuth } from '../../context/AuthContext';
import useIsAdmin from '../../hooks/use_is_admin';
import useIsNodeAdmin from '../../hooks/use_is_node_admin';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useNotifications } from '../../hooks/use_notifications';
import { invalidateCache } from '../../utils/api_cache';
import { useApi } from '../../hooks/use_api';
import { usePersonFields } from '../../hooks/use_person_fields';
import getEnv from '../../env';
import { MergeRequestsBanner, UseProposals } from './SplitMerge';

const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';

// The raw-JSON "Other attributes" row is hidden for now; flip to bring it back.
const SHOW_OTHER_ATTRIBUTES = false;

// Same rules as CreateEntityForm's URL / email Form.Item validators.
const validateUrl = v => {
    try {
        const u = new URL(v);
        return ['http:', 'https:'].includes(u.protocol) ? null : 'Please enter a valid URL';
    } catch {
        return 'Please enter a valid URL';
    }
};
const validateEmail = v => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Please enter a valid email');

// A place as a Google Maps link, or null when it has no coordinates.
function placeLink(place, key) {
    if (!place || typeof place !== 'object' || place.lat == null || place.lng == null) return null;
    return (
        <a key={key} href={`https://www.google.com/maps?q=${place.lat},${place.lng}`} target="_blank" rel="noopener noreferrer">
            {/* Values from the earlier map picker have no label. */}
            {place.label || `${place.lat}, ${place.lng}`}
        </a>
    );
}

// A custom Person field's value for display, or null when unset/invalid.
function renderPersonFieldValue(field, raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    if (field.field_type === 'geo') return placeLink(raw);
    if (field.field_type === 'geo_multi') {
        const links = (Array.isArray(raw) ? raw : []).map((p, i) => placeLink(p, i)).filter(Boolean);
        return links.length ? <Space size={[12, 4]} wrap>{links}</Space> : null;
    }
    if (field.field_type === 'date') return new Date(raw).toLocaleDateString();
    return String(raw);
}

// One wrapper for every state of the page — loading, not-found and the loaded
// entity all sit in the same centred column on the plain themed page.
function Stage({ children }) {
    return <div className="entity-stage">{children}</div>;
}

function Entity() {
    const api = useApi();
    const { entityId } = useParams();
    const navigate = useNavigate();
    const { userPersonEntity, user, refreshProfile } = useAuth();
    const personFields = usePersonFields();
    const [entity, setEntity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [connectedEntities, setConnectedEntities] = useState([]);
    const [connectedEntitiesLoading, setConnectedEntitiesLoading] = useState(false);
    const { isAdmin, loading: adminLoading, refresh: refreshIsAdmin } = useIsAdmin(entityId);
    // Node (super) admin is about the profile's owner, so it's shown to everyone.
    const isNodeAdmin = useIsNodeAdmin(entityId, entity?.entity_type === 'person' && !entity?.is_remote);
    const { isServerAdmin } = useSuperAdmins();
    // Person-only node: no connection can exist, so don't ask for them.
    // ConnectedEntities self-hides on the same flag (it still renders the Invite
    // action for a group entity left over on a downgraded node).
    const { connectionsEnabled, featureEntityListing, federationEnabled, federationShareAllListed } = useNodeInfo();
    const [claimLinkBusy, setClaimLinkBusy] = useState(null); // null | 'email' | 'link' | 'revoke'
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [claimLinkResult, setClaimLinkResult] = useState(null); // { claim_url, expires_at, email_sent }
    const [userConnectionStatus, setUserConnectionStatus] = useState(null);
    // The caller's own row in this group's connections (the link "Leave"
    // removes), with whether they are its last admin. null until known.
    const [membership, setMembership] = useState(null);
    const [requestingJoin, setRequestingJoin] = useState(false);
    const [requestingFederated, setRequestingFederated] = useState(false);
    const [startingMessage, setStartingMessage] = useState(false);
    const [isOwnPersonEntity, setIsOwnPersonEntity] = useState(false);
    const [archivingBusy, setArchivingBusy] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [extractedContent, setExtractedContent] = useState(null);
    const [extractModalOpen, setExtractModalOpen] = useState(false);
    const [notifEnabled, setNotifEnabled] = useState(false);
    const { fetchPreferences, setPreference, unreadByEntity } = useNotifications();
    // undefined = not checked yet, null = confirmed no channel exists, number = existing DM channel's entity id.
    const [dmChannelId, setDmChannelId] = useState(undefined);
    const userId = localStorage.getItem('id');

    useEffect(() => {
        api.get(`/api/entities/${entityId}`, { cache: true })
            .then(entityData => { setEntity(entityData); setLoading(false); })
            .catch(err => {
                console.warn('API unavailable, loading mock entity data:', err.message);
                return fetch('/mock-entity.json').then(r => r.json()).then(entityData => {
                    if (entityData) { setEntity(entityData); setLoading(false); }
                });
            })
            .catch(err => { console.error('Failed to load mock entity data:', err); setLoading(false); });
    }, [entityId]);

    useEffect(() => {
        if (entity?.entity_type === 'person') {
            setIsOwnPersonEntity(entity.user_id === userId);
        } else {
            setIsOwnPersonEntity(false);
        }
    }, [entity, userId]);

    useEffect(() => {
        if (!entityId || !user?.id) return;
        fetchPreferences().then(prefs => {
            setNotifEnabled(prefs[Number(entityId)] === true);
        });
    }, [entityId, user?.id]);

    // The caller's own standing in this group, read off its connection list.
    // Also re-run after an admin changes the list (handleConnectionUpdated),
    // so "Leave" unlocks as soon as another admin is appointed.
    const applyMembership = (data, groupType) => {
        const myConn = userPersonEntity?.id ? data.find(c => c.entity.id === userPersonEntity.id) : null;
        if (!myConn) setUserConnectionStatus('none');
        else if (myConn.connected_status) setUserConnectionStatus('connected');
        else setUserConnectionStatus('pending');
        setMembership(myConn ? { connection: myConn, soleAdmin: isSoleAdmin(myConn, data, groupType) } : null);
    };

    useEffect(() => {
        setMembership(null);
        if (!userPersonEntity?.id || !entity || !['context', 'relationship'].includes(entity.entity_type)) return;
        api.get(`/api/entities/${entityId}/connected`, { cache: true })
            .then(data => applyMembership(data, entity.entity_type))
            .catch(() => setUserConnectionStatus('none'));
    }, [entityId, userPersonEntity?.id, entity?.entity_type]);

    useEffect(() => {
        if (!userPersonEntity?.id || !entity || entity.entity_type !== 'person' || isOwnPersonEntity || entity.is_remote) {
            setDmChannelId(undefined);
            return;
        }
        api.get('/api/connections/dm', { params: { person_a_id: userPersonEntity.id, person_b_id: Number(entityId) } })
            .then(res => setDmChannelId(res.entity.id))
            .catch(() => setDmChannelId(null));
    }, [entityId, userPersonEntity?.id, entity, isOwnPersonEntity]);

    useEffect(() => {
        if (!isAdmin || !connectionsEnabled) return;
        setConnectedEntitiesLoading(true);
        api.get(`/api/entities/${entityId}/connected`, { cache: true })
            .then(data => setConnectedEntities(data))
            .catch(() => {})
            .finally(() => setConnectedEntitiesLoading(false));
    }, [entityId, isAdmin, connectionsEnabled]);

    const handleRequestJoin = async () => {
        if (!userPersonEntity?.id) return;
        setRequestingJoin(true);
        try {
            await api.post('/api/connections', {
                entity_a_id: userPersonEntity.id,
                entity_b_id: Number(entityId),
                connected_status: false,
                created_by: userPersonEntity.id,
            });
            setUserConnectionStatus('pending');
            invalidateCache(`${API_URL}/api/entities/${entityId}/connected`);
            message.success('Connection request sent');
        } catch (err) {
            message.error(err.message || 'Failed to send request');
        } finally {
            setRequestingJoin(false);
        }
    };

    const handleRequestFederated = async () => {
        if (!userPersonEntity?.id) return;
        setRequestingFederated(true);
        try {
            await api.post('/api/connections/federated', {
                person_entity_id: userPersonEntity.id,
                shadow_entity_id: Number(entityId),
            });
            setUserConnectionStatus('pending');
            invalidateCache(`${API_URL}/api/entities/${entityId}/connected`);
            message.success('Connection request sent to the remote server');
        } catch (err) {
            message.error(err.message || 'Failed to send request');
        } finally {
            setRequestingFederated(false);
        }
    };

    const handleStartMessage = async () => {
        if (!userPersonEntity?.id) return;
        setStartingMessage(true);
        try {
            const res = await api.post('/api/connections/dm', {
                requester_person_id: userPersonEntity.id,
                target_person_id: Number(entityId),
            });
            navigate(`/messages/${res.entity.id}`);
        } catch (err) {
            message.error(err.message || 'Failed to start conversation');
        } finally {
            setStartingMessage(false);
        }
    };

    const refreshEntity = async () => {
        invalidateCache(`${API_URL}/api/entities/${entityId}`);
        const entityData = await api.get(`/api/entities/${entityId}`, { cache: true });
        setEntity(entityData);
        // The navbar's "me" button renders AuthContext's copy of the person
        // entity (picture, name), loaded at login — keep it in step when the
        // entity being edited is that one.
        if (userPersonEntity && Number(entityId) === userPersonEntity.id) {
            refreshProfile?.();
        }
    };

    const handleToggleNotif = (checked) => {
        setNotifEnabled(checked);
        setPreference(Number(entityId), checked).catch(() => setNotifEnabled(!checked));
    };

    const handleToggleArchive = async () => {
        setArchivingBusy(true);
        try {
            await api.patch(`/api/entities/${entityId}/archive`, undefined, { params: { user_id: userId } });
            await refreshEntity();
        } catch {
            message.error('Failed to update archive status');
        } finally {
            setArchivingBusy(false);
        }
    };

    const copyClaimUrl = url => {
        // clipboard.writeText needs a secure context (https/localhost) — fall back
        // to telling the admin to copy the (already visible, selectable) input.
        navigator.clipboard?.writeText(url)
            .then(() => message.success('Invite link copied'))
            .catch(() => message.warning('Could not copy automatically — select the link and copy it'));
    };

    // sendEmail=false mints/returns the same link without emailing, so an entity
    // with no email on file can still be invited by pasting the link somewhere.
    const handleClaimLink = async (sendEmail, emailOverride) => {
        setClaimLinkBusy(sendEmail ? 'email' : 'link');
        try {
            // Body is the wording from InviteEmailModal; omitted entirely for
            // "Get invite link", which never sends anything to render.
            const data = await api.post(`/api/entities/${entityId}/claim-link`, emailOverride && {
                subject: emailOverride.subject,
                body: emailOverride.body,
                save_as_default: !!emailOverride.saveAsDefault,
            }, {
                params: { user_id: userId, send_email: sendEmail },
            });
            setClaimLinkResult(data);
            setInviteModalOpen(false);
            if (data.email_sent) message.success(`Invite emailed to ${entity.email}`);
            else if (sendEmail) message.warning('Email not sent (SMTP not configured on this node) — share the link below manually');
            else copyClaimUrl(data.claim_url);
        } catch (err) {
            message.error(err.message || 'Failed to generate invite link');
        } finally {
            setClaimLinkBusy(null);
        }
    };

    const handleRevokeClaimLink = async () => {
        setClaimLinkBusy('revoke');
        try {
            await api.del(`/api/entities/${entityId}/claim-link`, { params: { user_id: userId } });
            message.success('Invite link revoked');
            setClaimLinkResult(null);
        } catch {
            message.error('Failed to revoke invite link');
        } finally {
            setClaimLinkBusy(null);
        }
    };


    const handleExtractContent = async () => {
        setExtracting(true);
        try {
            const data = await api.post(`/api/entities/${entityId}/extract-url`);
            setExtractedContent(data);
            setExtractModalOpen(true);
        } catch (err) {
            message.error(err.message || 'Could not extract content');
        } finally {
            setExtracting(false);
        }
    };


    const handleConnectionUpdated = async () => {
        invalidateCache(`${API_URL}/api/entities/${entityId}/connected`);
        const data = await api.get(`/api/entities/${entityId}/connected`, { cache: true });
        setConnectedEntities(data);
        if (['context', 'relationship'].includes(entity?.entity_type)) applyMembership(data, entity.entity_type);
    };

    // After leaving: back to "Request to join", and re-ask whether the caller
    // still administers the group (they may, through a relationship) so the
    // admin-only panels follow suit instead of waiting out the cache.
    const handleLeft = async () => {
        setMembership(null);
        setUserConnectionStatus('none');
        invalidateCache(`${API_URL}/api/entities/${entityId}/connected`);
        refreshIsAdmin();
        if (isAdmin) await handleConnectionUpdated().catch(() => {});
    };

    // Close always lands on the homepage. It used to step history back when the
    // previous page was another entity, on the theory that closing a stacked
    // panel should reveal the one underneath — but that made X mean two
    // different things depending on how you arrived, and following a few
    // connections left it unclear how many more presses it would take to get
    // out. One destination, always.
    const handleClose = () => navigate('/');

    if (loading) return <Stage><Spin size="large" style={{ marginTop: '22vh' }} /></Stage>;
    if (!entity) return <Stage><Typography.Text style={{ marginTop: '22vh' }}>Entity not found</Typography.Text></Stage>;

    const isRemote = !!entity.is_remote;
    const isGroupEntity = ['context', 'relationship'].includes(entity.entity_type);
    const showComms = isGroupEntity;
    const isUnclaimedPerson = entity.entity_type === 'person' && !entity.user_id;
    // Superadmins can manage placeholder person entities (e.g. from CSV import) that
    // no one has claimed yet — no owner exists to gate a normal isOwnPersonEntity edit.
    const showSuperadminClaimToolbar = isServerAdmin && isUnclaimedPerson && !isRemote;
    // Remote shadow rows are read-only caches (editing/joining a remote entity is Phase 2b).
    const showEdit = ((isAdmin && entity.entity_type !== 'person') || isOwnPersonEntity || showSuperadminClaimToolbar) && !isRemote;
    const showJoin = !isAdmin && isGroupEntity && userPersonEntity && ['pending', 'none'].includes(userConnectionStatus) && !isRemote;
    // "Let go": any accepted member, admin or not, may leave — except the last
    // admin (LeaveEntityButton explains). Never on the hidden DM relationship,
    // which only ever surfaces through Messages, nor on a remote shadow, whose
    // membership lives on the other server.
    const showLeave = isGroupEntity && !isRemote && !entity.attributes?.is_dm
        && userConnectionStatus === 'connected' && !!membership?.connection;
    // Cross-server: a remote group can be joined via the federation handshake.
    const showRequestFederated = isRemote && isGroupEntity && userPersonEntity && userConnectionStatus === 'none';
    const showRemotePending = isRemote && isGroupEntity && userConnectionStatus === 'pending';
    const showRemoteConnected = isRemote && isGroupEntity && userConnectionStatus === 'connected';
    // A pending person (invited but not joined, or not yet approved) can't be
    // sent a new DM; an already-started conversation stays reachable via "Message".
    const isPendingPerson = !entity.user_id || entity.registration_approved === false;
    const showMessage = entity.entity_type === 'person' && !isOwnPersonEntity && !isRemote && !!userPersonEntity?.id
        && (!isPendingPerson || !!dmChannelId);

    const isPerson = entity.entity_type === 'person';
    const urlNode = (
        <Space size={4} align="center">
            <Tooltip title="Extract content from URL">
                <Button type="text" size="small" icon={<ReadOutlined />} loading={extracting} onClick={handleExtractContent} style={{ padding: '0 2px' }} />
            </Tooltip>
            <Typography.Link href={entity.url} target="_blank" rel="noopener noreferrer" className="entity-url">
                {entity.url}
            </Typography.Link>
        </Space>
    );
    // Every field is listed, empty ones as "—", so gaps are visible.
    const empty = <Typography.Text type="secondary">—</Typography.Text>;

    // Inline editing: same permission as the Edit modal. Each field saves on
    // its own with a one-key PUT — attributes are merged server-side, so one
    // custom field never clobbers the others, and a description change
    // re-embeds the default description.
    const saveField = async (payload, label) => {
        try {
            await api.put(`/api/entities/${entity.id}`, payload, { params: { user_id: user?.id } });
            await refreshEntity();
            message.success(`${label} updated`);
        } catch (err) {
            message.error(err?.message || `Could not update ${label.toLowerCase()}`);
            throw err;
        }
    };
    const editable = (label, field, display, extra = {}) => (
        <InlineEditField
            canEdit={showEdit}
            value={entity[field]}
            display={display}
            onSave={v => saveField({ [field]: v }, label)}
            placeholder={label}
            {...extra}
        />
    );

    // "Other attributes": attributes keys that aren't custom Person fields,
    // edited as JSON. Attributes merge server-side, so a key removed in the
    // editor has to be sent as null to actually clear it.
    const personFieldKeys = new Set(personFields.map(f => f.key));
    const otherAttributes = Object.fromEntries(
        Object.entries(entity.attributes || {})
            .filter(([k, v]) => !(isPerson && personFieldKeys.has(k)) && v !== null),
    );
    const saveOtherAttributes = async text => {
        let next;
        try {
            next = text ? JSON.parse(text) : {};
        } catch {
            message.error('Invalid JSON');
            throw new Error('Invalid JSON');
        }
        if (!next || typeof next !== 'object' || Array.isArray(next)) {
            message.error('Attributes must be a JSON object');
            throw new Error('Attributes must be a JSON object');
        }
        const cleared = Object.fromEntries(Object.keys(otherAttributes).filter(k => !(k in next)).map(k => [k, null]));
        await saveField({ attributes: { ...cleared, ...next } }, 'Attributes');
    };

    // Your own profile: flag what's still blank so people finish filling it in.
    // Description plus the node's custom Person fields — the same rows below.
    const nudgeProfile = isOwnPersonEntity && !isRemote;
    const profileChecklist = nudgeProfile ? [
        { key: 'description', label: 'Description', filled: !isBlank(entity.description) },
        ...personFields.map(f => ({ key: `field:${f.key}`, label: f.label, filled: !isBlank(entity.attributes?.[f.key]) })),
    ] : [];
    const missingProfileItems = profileChecklist.filter(i => !i.filled);
    const missingProfileKeys = new Set(missingProfileItems.map(i => i.key));
    const scrollToField = key => document.getElementById(`entity-field-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // No 'Type' row: the card head shows that tag directly above this list, and
    // the two sat far enough apart in the old table for both to earn their place.
    const detailsItems = [
        // Visible to everyone; editing follows the usual edit permission. Not
        // offered on people, same as URL below: an address still reaches a
        // person through CSV import, and is still what "Send email invite"
        // uses — it just isn't a field anyone types here.
        !isPerson
            && { key: 'email', label: 'Email', children: editable('Email', 'email', entity.email || empty, { validate: validateEmail }) },
        // Not offered on people (matching the create/edit form and the built-in
        // list on the Person fields tab). Any url already stored on a person
        // stays on the record and in the API — it just isn't shown here.
        !isPerson
            && { key: 'url', label: 'URL', children: editable('URL', 'url', entity.url ? urlNode : empty, { validate: validateUrl }) },
        // An occasion (todo D12): a group with a start or an end. Open-ended
        // when neither is set; the calendar shows it from its start.
        isGroupEntity && (showEdit || entity.starts_at || entity.ends_at) && {
            key: 'when',
            label: 'When',
            children: showEdit ? (
                <DatePicker.RangePicker
                    size="small"
                    allowEmpty={[true, true]}
                    value={[entity.starts_at ? dayjs(entity.starts_at) : null, entity.ends_at ? dayjs(entity.ends_at) : null]}
                    placeholder={['From (open)', 'Until (open)']}
                    onChange={v => saveField({
                        starts_at: v?.[0] ? v[0].toISOString() : null,
                        ends_at: v?.[1] ? v[1].toISOString() : null,
                    }, 'When').catch(() => {})}
                />
            ) : (
                <Typography.Text>
                    {entity.starts_at ? dayjs(entity.starts_at).format('D MMM YYYY') : '…'} – {entity.ends_at ? dayjs(entity.ends_at).format('D MMM YYYY') : '…'}
                </Typography.Text>
            ),
        },
        {
            key: 'description',
            // An entity's one description is its profile (todo D4).
            label: 'Profile',
            children: editable('Description', 'description', entity.description ? (
                <Typography.Paragraph
                    style={{ marginBottom: 0 }}
                    ellipsis={{ rows: 3, expandable: 'collapsible', symbol: expanded => expanded ? 'Show less' : 'Show more' }}
                >
                    {entity.description}
                </Typography.Paragraph>
            ) : empty, {
                type: 'textarea',
                // The backend skips a null description (it means "unchanged"),
                // so clearing has to send an empty string.
                onSave: v => saveField({ description: v ?? '' }, 'Description'),
                emptyPrompt: nudgeProfile ? 'Add a description' : undefined,
            }),
        },
        ...(isPerson ? personFields.map(f => ({
            key: `field:${f.key}`,
            label: f.label,
            children: (
                <InlineEditField
                    canEdit={showEdit}
                    type={f.field_type}
                    options={f.options}
                    value={entity.attributes?.[f.key]}
                    display={renderPersonFieldValue(f, entity.attributes?.[f.key]) ?? empty}
                    // null clears the key (merge keeps explicit nulls as "unset").
                    onSave={v => saveField({ attributes: { [f.key]: v } }, f.label)}
                    placeholder={f.label}
                    emptyPrompt={nudgeProfile ? `Add ${f.label}` : undefined}
                />
            ),
        })) : []),
        // ── Editor-only rows: everything the Edit modal could change ──
        showEdit && featureEntityListing && {
            key: 'listed',
            label: 'Listed',
            children: (
                <Tooltip title="Listed entities appear in public/discoverable views. Unlisted ones are only visible to people who already know them.">
                    <Switch
                        checked={!!entity.is_listed}
                        checkedChildren="Listed"
                        unCheckedChildren="Unlisted"
                        onChange={checked => saveField({ is_listed: checked }, 'Listed status').catch(() => {})}
                    />
                </Tooltip>
            ),
        },
        // Federation: whether peers may find, view and join this entity. Its own
        // decision, separate from Listed, which only concerns this node's members
        // (backend services/federation_sharing.py). A node whose policy shares
        // everything it lists shows that policy in place of the switch.
        showEdit && federationEnabled && {
            key: 'shared',
            label: 'Shared',
            children: federationShareAllListed ? (
                <Tooltip title="This node shares every listed entity with its federation peers, so sharing follows the Listed switch.">
                    <Switch checked={!!entity.is_listed} checkedChildren="Shared" unCheckedChildren="Not shared" disabled />
                </Tooltip>
            ) : (
                <Tooltip title="Shared entities can be found, viewed and joined from other federation servers. Listed is separate: it only decides what this node's own members see.">
                    <Switch
                        checked={!!entity.shared_to_federation}
                        checkedChildren="Shared"
                        unCheckedChildren="Not shared"
                        onChange={checked => saveField({ shared_to_federation: checked }, 'Federation sharing').catch(() => {})}
                    />
                </Tooltip>
            ),
        },
        SHOW_OTHER_ATTRIBUTES && showEdit && {
            key: 'attributes',
            label: 'Other attributes',
            children: (
                <InlineEditField
                    canEdit
                    type="textarea"
                    value={Object.keys(otherAttributes).length ? JSON.stringify(otherAttributes, null, 2) : ''}
                    placeholder='{"key": "value"}'
                    display={Object.keys(otherAttributes).length ? (
                        <pre className="entity-attributes-json">{JSON.stringify(otherAttributes, null, 2)}</pre>
                    ) : empty}
                    onSave={saveOtherAttributes}
                />
            ),
        },
        // Archiving mirrors the modal: entity admins, non-person entities only.
        isAdmin && !isPerson && !isRemote && {
            key: 'archived',
            label: 'Archived',
            children: (
                <Switch
                    checked={!!entity.is_archived}
                    loading={archivingBusy}
                    checkedChildren="Archived"
                    unCheckedChildren="Active"
                    onChange={handleToggleArchive}
                />
            ),
        },
    ].filter(Boolean);

    return (
        <Stage>
        <div className="entity-page">

            {/* Corner icon actions — pinned to the viewport, see Entity.css */}
            <div className="entity-corner-actions">
                {showMessage && (
                    <Badge count={dmChannelId ? (unreadByEntity[dmChannelId] || 0) : 0} size="small" offset={[-4, 2]}>
                        <Button
                            type="text"
                            className="entity-text-btn"
                            icon={<MessageOutlined />}
                            onClick={handleStartMessage}
                            loading={startingMessage}
                        >
                            {dmChannelId === null ? 'Start messaging' : 'Message'}
                        </Button>
                    </Badge>
                )}
                {/* Keep this entity at hand: an entry in the caller's own
                    sidebar (hooks/use_sidebar_pins.js). */}
                <PinToSidebarButton entityId={entity.id} />
                <div className="entity-actions-sep" />
                <Tooltip title="Close">
                    <Button type="text" className="entity-icon-btn entity-icon-btn--close" icon={<CloseOutlined />} onClick={handleClose} />
                </Tooltip>
            </div>

            {/* Profile picture where the claim page puts the Vellby mark: the
                uploaded image, or a generated avatar until there is one. */}
            <EntityImageEditor entity={entity} canEdit={showEdit} onChanged={refreshEntity} />

            {/* ── Identity card ── */}
            <Card className="glass-panel entity-card" styles={{ body: { padding: '28px 28px 24px' } }}>
                <div className="entity-card-head">
                    {editable('Name', 'name', (
                        <Typography.Title level={3} className="entity-name">
                            {entity.name}
                        </Typography.Title>
                    ), { required: true })}
                    <div className="entity-meta-row">
                        <Tag>{entity.entity_type}</Tag>
                        {entity.is_remote && entity.home_server_id && (
                            <Tooltip title="Cached from another federation server (read-only)">
                                <Tag color="geekblue">@{entity.home_server_id} · cached</Tag>
                            </Tooltip>
                        )}
                        {entity.is_archived && <Tag color="warning">Archived</Tag>}
                        {/* Entity admin, not node admin — for a person that is just
                            "this is your own profile", so it would mislabel users. */}
                        {!adminLoading && isAdmin && entity.entity_type !== 'person' && <Tag color="blue">admin</Tag>}
                        {isNodeAdmin && (
                            <Tooltip title="Administers this node">
                                <Tag color="gold">node admin</Tag>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {missingProfileItems.length > 0 && (
                    <div className="entity-profile-nudge">
                        <div className="entity-profile-nudge__head">
                            <Typography.Text strong>Complete your profile</Typography.Text>
                            <Typography.Text type="secondary">
                                {profileChecklist.length - missingProfileItems.length} of {profileChecklist.length} filled in
                            </Typography.Text>
                        </div>
                        <Progress
                            percent={Math.round(100 * (profileChecklist.length - missingProfileItems.length) / profileChecklist.length)}
                            showInfo={false}
                            size="small"
                        />
                        <div className="entity-profile-nudge__missing">
                            {missingProfileItems.map(i => (
                                <Button key={i.key} size="small" type="dashed" onClick={() => scrollToField(i.key)}>
                                    {i.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Same rows the bordered Descriptions table used to render —
                    detailsItems is plain {key, label, children} data, so only
                    the presentation changes: label above value, no cell grid. */}
                {detailsItems.map(item => (
                    <div
                        className={`entity-field${missingProfileKeys.has(item.key) ? ' entity-field--missing' : ''}`}
                        id={`entity-field-${item.key}`}
                        key={item.key}
                    >
                        <div className="entity-field-label t-label-upper">{item.label}</div>
                        <div className="entity-field-value">{item.children}</div>
                    </div>
                ))}
            </Card>

            {/* Your own person entity: point at the account page for
                login/security settings, which don't live on the entity. */}
            {isOwnPersonEntity && (
                <Alert
                    className="entity-account-banner"
                    type="info"
                    showIcon
                    title="Manage your account"
                    description="To change your password, set up alternate logins, or see the devices you're currently signed in on, go to your account page."
                    action={(
                        <Button icon={<SettingOutlined />} onClick={() => navigate('/account')}>
                            Go to account
                        </Button>
                    )}
                />
            )}

            {/* ── Superadmin invite panel: person entity with no login bound ── */}
            {showSuperadminClaimToolbar && (
                <div className="entity-invite-panel">
                    <div className="entity-invite-panel__row">
                        <div className="entity-invite-panel__status">
                            <Tag color="gold" style={{ marginInlineEnd: 0 }}>Pending</Tag>
                            <Typography.Text type="secondary">No login is linked to this person yet.</Typography.Text>
                        </div>
                        <Space wrap size={8}>
                            <Tooltip title={entity.email ? '' : 'This person has no email on file — import people with an email column to send invites by email, or use Get invite link'}>
                                <Button
                                    icon={<MailOutlined />}
                                    disabled={!entity.email}
                                    onClick={() => setInviteModalOpen(true)}
                                >
                                    Send email invite
                                </Button>
                            </Tooltip>
                            <Tooltip title="Generate an invite link to share manually — no email needed">
                                <Button
                                    icon={<LinkOutlined />}
                                    loading={claimLinkBusy === 'link'}
                                    onClick={() => handleClaimLink(false)}
                                >
                                    Get invite link
                                </Button>
                            </Tooltip>
                        </Space>
                    </div>

                    {claimLinkResult && (
                        <div className="entity-invite-panel__result">
                            <Input
                                readOnly
                                value={claimLinkResult.claim_url}
                                onFocus={e => e.target.select()}
                                addonAfter={
                                    <Tooltip title="Copy link">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            style={{ padding: 0 }}
                                            onClick={() => copyClaimUrl(claimLinkResult.claim_url)}
                                        />
                                    </Tooltip>
                                }
                            />
                            <div className="entity-invite-panel__meta">
                                <Typography.Text type="secondary">
                                    Single use · expires {new Date(claimLinkResult.expires_at).toLocaleString()}
                                </Typography.Text>
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={claimLinkBusy === 'revoke'}
                                    onClick={handleRevokeClaimLink}
                                >
                                    Revoke
                                </Button>
                            </div>
                        </div>
                    )}

                    <InviteEmailModal
                        open={inviteModalOpen}
                        entity={entity}
                        sending={claimLinkBusy === 'email'}
                        onCancel={() => setInviteModalOpen(false)}
                        onSend={wording => handleClaimLink(true, wording)}
                    />
                </div>
            )}

            {/* ── Join prompt (non-admin) ── */}
            {showJoin && (
                <div className="entity-toolbar">
                    {(
                        userConnectionStatus === 'pending' ? (
                            <Button icon={<ClockCircleOutlined />} disabled>Request pending</Button>
                        ) : (
                            <Button icon={<UserAddOutlined />} type="primary" onClick={handleRequestJoin} loading={requestingJoin}>
                                Request to join
                            </Button>
                        )
                    )}
                </div>
            )}

            {/* ── Leave (any accepted member) ── */}
            {showLeave && (
                <div className="entity-toolbar">
                    <LeaveEntityButton
                        entity={entity}
                        connection={membership.connection}
                        soleAdmin={membership.soleAdmin}
                        onLeft={handleLeft}
                    />
                </div>
            )}

            {/* ── Cross-server join (remote group) ── */}
            {(showRequestFederated || showRemotePending || showRemoteConnected) && (
                <div className="entity-toolbar">
                    {showRemoteConnected ? (
                        <Tag color="success">Connected</Tag>
                    ) : showRemotePending ? (
                        <Button icon={<ClockCircleOutlined />} disabled>Request pending</Button>
                    ) : (
                        <Button icon={<UserAddOutlined />} type="primary" onClick={handleRequestFederated} loading={requestingFederated}>
                            Request connection
                        </Button>
                    )}
                </div>
            )}

            {/* ── Communication panel ── */}
            {showComms && (!isRemote || showRemoteConnected) && (
                <EntityComms
                    entityId={entityId}
                    entity={entity}
                    isAdmin={isAdmin && !isRemote}
                    notifEnabled={notifEnabled}
                    onToggleNotif={handleToggleNotif}
                />
            )}

            {/* ── Extracted content modal ── */}
            <Modal
                title={extractedContent?.title || 'Extracted Content'}
                open={extractModalOpen}
                onCancel={() => setExtractModalOpen(false)}
                width={760}
                footer={showEdit && extractedContent?.text ? (
                    <Button
                        type="primary"
                        onClick={() => saveField({ description: extractedContent.text }, 'Description')
                            .then(() => setExtractModalOpen(false))
                            .catch(() => {})}
                    >
                        Use as description
                    </Button>
                ) : null}
            >
                {extractedContent && (
                    <div>
                        <Space wrap style={{ marginBottom: 12 }}>
                            {extractedContent.author && <Typography.Text type="secondary">By {extractedContent.author}</Typography.Text>}
                            {extractedContent.date && <Typography.Text type="secondary">{extractedContent.date}</Typography.Text>}
                            {extractedContent.language && <Tag>{extractedContent.language}</Tag>}
                        </Space>
                        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                                {extractedContent.text}
                            </Typography.Paragraph>
                        </div>
                    </div>
                )}
            </Modal>

            {/* ── Inline links (admin only) ── */}
            {isAdmin && isGroupEntity && !isRemote && (
                <>
                    <MergeRequestsBanner entity={entity} isAdmin={isAdmin} onDone={handleConnectionUpdated} />
                    <UseProposals entity={entity} isAdmin={isAdmin} connections={connectedEntities} onDone={handleConnectionUpdated} />
                </>
            )}
            {isAdmin && (
                <div className="entity-links">
                    {connectedEntitiesLoading ? (
                        <div style={{ padding: '24px 0', textAlign: 'center' }}><Spin size="small" /></div>
                    ) : (
                        <ConnectedEntities
                            connectedEntities={connectedEntities}
                            isAdmin={isAdmin}
                            entityType={entity.entity_type}
                            currentEntityId={Number(entityId)}
                            entityName={entity.name}
                            onConnectionUpdated={handleConnectionUpdated}
                            inviteToken={entity.invite_token}
                            onInviteUpdated={refreshEntity}
                        />
                    )}
                </div>
            )}
        </div>
        </Stage>
    );
}

export default Entity;
